import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './number-of-bedrooms.reducer';

export const NumberOfBedrooms = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const numberOfBedroomsList = useAppSelector(state => state.numberOfBedrooms.entities);
  const loading = useAppSelector(state => state.numberOfBedrooms.loading);
  const totalItems = useAppSelector(state => state.numberOfBedrooms.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="number-of-bedrooms-heading" data-cy="NumberOfBedroomsHeading">
        <Translate contentKey="apiLocationApp.numberOfBedrooms.home.title">Number Of Bedrooms</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="apiLocationApp.numberOfBedrooms.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/number-of-bedrooms/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="apiLocationApp.numberOfBedrooms.home.createLabel">Create new Number Of Bedrooms</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {numberOfBedroomsList && numberOfBedroomsList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="apiLocationApp.numberOfBedrooms.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('geographicLevel')}>
                  <Translate contentKey="apiLocationApp.numberOfBedrooms.geographicLevel">Geographic Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographicLevel')} />
                </th>
                <th className="hand" onClick={sort('date')}>
                  <Translate contentKey="apiLocationApp.numberOfBedrooms.date">Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('date')} />
                </th>
                <th className="hand" onClick={sort('geography')}>
                  <Translate contentKey="apiLocationApp.numberOfBedrooms.geography">Geography</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geography')} />
                </th>
                <th className="hand" onClick={sort('geographyCode')}>
                  <Translate contentKey="apiLocationApp.numberOfBedrooms.geographyCode">Geography Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographyCode')} />
                </th>
                <th className="hand" onClick={sort('totalAllHouseholds')}>
                  <Translate contentKey="apiLocationApp.numberOfBedrooms.totalAllHouseholds">Total All Households</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalAllHouseholds')} />
                </th>
                <th className="hand" onClick={sort('noBedroom')}>
                  <Translate contentKey="apiLocationApp.numberOfBedrooms.noBedroom">No Bedroom</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('noBedroom')} />
                </th>
                <th className="hand" onClick={sort('oneBedroom')}>
                  <Translate contentKey="apiLocationApp.numberOfBedrooms.oneBedroom">One Bedroom</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('oneBedroom')} />
                </th>
                <th className="hand" onClick={sort('twoBedrooms')}>
                  <Translate contentKey="apiLocationApp.numberOfBedrooms.twoBedrooms">Two Bedrooms</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('twoBedrooms')} />
                </th>
                <th className="hand" onClick={sort('threeBedrooms')}>
                  <Translate contentKey="apiLocationApp.numberOfBedrooms.threeBedrooms">Three Bedrooms</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('threeBedrooms')} />
                </th>
                <th className="hand" onClick={sort('fourBedrooms')}>
                  <Translate contentKey="apiLocationApp.numberOfBedrooms.fourBedrooms">Four Bedrooms</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('fourBedrooms')} />
                </th>
                <th className="hand" onClick={sort('fiveOrMoreBedrooms')}>
                  <Translate contentKey="apiLocationApp.numberOfBedrooms.fiveOrMoreBedrooms">Five Or More Bedrooms</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('fiveOrMoreBedrooms')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {numberOfBedroomsList.map((numberOfBedrooms, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/number-of-bedrooms/${numberOfBedrooms.id}`} color="link" size="sm">
                      {numberOfBedrooms.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`apiLocationApp.GeographicLevel.${numberOfBedrooms.geographicLevel}`} />
                  </td>
                  <td>{numberOfBedrooms.date}</td>
                  <td>{numberOfBedrooms.geography}</td>
                  <td>{numberOfBedrooms.geographyCode}</td>
                  <td>{numberOfBedrooms.totalAllHouseholds}</td>
                  <td>{numberOfBedrooms.noBedroom}</td>
                  <td>{numberOfBedrooms.oneBedroom}</td>
                  <td>{numberOfBedrooms.twoBedrooms}</td>
                  <td>{numberOfBedrooms.threeBedrooms}</td>
                  <td>{numberOfBedrooms.fourBedrooms}</td>
                  <td>{numberOfBedrooms.fiveOrMoreBedrooms}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/number-of-bedrooms/${numberOfBedrooms.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/number-of-bedrooms/${numberOfBedrooms.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/number-of-bedrooms/${numberOfBedrooms.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="apiLocationApp.numberOfBedrooms.home.notFound">No Number Of Bedrooms found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={numberOfBedroomsList && numberOfBedroomsList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default NumberOfBedrooms;
